import React, { useState } from "react";
import { Mail, Phone, MapPin, Send, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { contactApi } from "../api";

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    // Honeypot: never displayed to humans. If a bot fills this, the backend
    // silently swallows the submission. Field name "website" matches a common
    // bot heuristic (auto-fill anything resembling a URL field).
    website: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      await contactApi.submit({
        name: formData.name,
        email: formData.email,
        subject: "Website Inquiry",
        message: formData.message,
        website: formData.website,
      });
      setIsSubmitted(true);
      setFormData({ name: "", email: "", message: "", website: "" });
    } catch (err) {
      // 429 = rate-limited by backend.
      if (err?.response?.status === 429) {
        setError("Too many submissions from your network. Please try again in an hour.");
      } else if (err?.response?.status === 400) {
        setError(err.response.data?.detail || "Please check your message and try again.");
      } else {
        setError("Failed to send message. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-[#fffaf5]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-[#3a2a26] mb-4">
            Contact Us
          </h2>
          <p className="text-[#7a6b62] max-w-2xl mx-auto">
            Have questions? We're here to help.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <h3 className="text-2xl font-bold text-[#3a2a26] mb-8">Get in Touch</h3>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#fdf2f4] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-[#b89a6d]" />
                </div>
                <div>
                  <div className="text-[#7a6b62] text-sm mb-1">Email</div>
                  <a href="mailto:everything@8eight8.net" className="text-[#3a2a26] hover:text-[#b89a6d] transition-colors">
                    everything@8eight8.net
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#fdf2f4] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-[#b89a6d]" />
                </div>
                <div>
                  <div className="text-[#7a6b62] text-sm mb-1">Phone</div>
                  <a href="tel:+18083012134" className="text-[#3a2a26] hover:text-[#b89a6d] transition-colors">
                    +1 (808) 301-2134
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#fdf2f4] rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-[#b89a6d]" />
                </div>
                <div>
                  <div className="text-[#7a6b62] text-sm mb-1">Business Address</div>
                  <p className="text-[#3a2a26]">
                    PeptidesForMedSpas LLC<br />
                    Honolulu, HI 96815<br />
                    United States
                  </p>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="mt-8 p-6 bg-white border border-[#f0d8d4] rounded-xl">
              <h4 className="text-[#3a2a26] font-semibold mb-3">Support Hours</h4>
              <p className="text-[#7a6b62] text-sm">
                Monday - Friday: 9:00 AM - 5:00 PM HST<br />
                Saturday - Sunday: Closed
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white border border-[#f0d8d4] rounded-2xl p-8">
            {isSubmitted ? (
              <div className="text-center py-12">
                <CheckCircle2 className="w-16 h-16 text-[#b89a6d] mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-[#3a2a26] mb-2">Message Sent!</h3>
                <p className="text-[#7a6b62]">We'll get back to you as soon as possible.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                {/* Honeypot — invisible to humans, irresistible to bots. Do not remove. */}
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: "-10000px",
                    top: "auto",
                    width: "1px",
                    height: "1px",
                    overflow: "hidden",
                  }}
                >
                  <label htmlFor="contact-website-hp">Website (leave blank)</label>
                  <input
                    type="text"
                    id="contact-website-hp"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#5a4a40] mb-2">Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-[#fffaf5] border border-[#e8c5b8] rounded-lg text-[#3a2a26] placeholder-[#a09186] focus:border-[#d4a5ad] focus:ring-1 focus:ring-[#d4a5ad] outline-none transition-colors"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#5a4a40] mb-2">Email</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 bg-[#fffaf5] border border-[#e8c5b8] rounded-lg text-[#3a2a26] placeholder-[#a09186] focus:border-[#d4a5ad] focus:ring-1 focus:ring-[#d4a5ad] outline-none transition-colors"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#5a4a40] mb-2">Message</label>
                  <textarea
                    required
                    rows={5}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-4 py-3 bg-[#fffaf5] border border-[#e8c5b8] rounded-lg text-[#3a2a26] placeholder-[#a09186] focus:border-[#d4a5ad] focus:ring-1 focus:ring-[#d4a5ad] outline-none transition-colors resize-none"
                    placeholder="How can we help you?"
                  />
                </div>

                {error && (
                  <p className="text-[#c47267] text-sm">{error}</p>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#d4a5ad] hover:bg-[#c4949c] text-white font-semibold py-6"
                >
                  {isSubmitting ? (
                    "Sending..."
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
